import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { debounce } from "../../Utils/helper";
import Search from "./../../components/common/Field/Search";
import {
  useGetBusinessDetailQuery,
  useLazyGetOutletOfBusinessQuery,
  usePostFavoriteBusinessMutation,
  usePostFavoritesLocationMutation,
} from "./../../services/apiSlice";

const Card = ({ item, isTranslate, business_id }) => {
  const navigate = useNavigate();

  const [onFavoriteBusiness] = usePostFavoriteBusinessMutation();
  const [onFavoriteLocation] = usePostFavoritesLocationMutation();

  const handleClick = async () => {
    if (item?.type === "BUSINESS") {
      await onFavoriteBusiness({ business_id: item?.id }).unwrap();
    }
    if (item?.type === "LOCATION") {
      await onFavoriteLocation({ location_id: item?.id }).unwrap();
    }
  };

  return (
    <div
      className={`container flex justify-between p-3 shadow-[0_0_20px_0_#0000001A] rounded-md cursor-pointer ${
        isTranslate ? "-translate-y-[30px]" : ""
      } bg-white gap-[18px]`}
    >
      <div
        className="flex flex-1 gap-[18px] items-center truncate"
        onClick={
          item?.type === "BUSINESS"
            ? null
            : () =>
                navigate(`/quest/location/${item.id}`, {
                  state: { businessId: business_id },
                })
        }
      >
        <img
          src={
            item?.avatar_data
              ? `${process.env.REACT_APP_URL_IMAGE}/${item?.avatar_data.file_path}`
              : "/images/brand.jpg"
          }
          className="size-[70px] rounded-[6px] border border-light-grey object-cover"
          alt=""
        />
        {item?.company_name && (
          <div className="space-y-1.5 w-full truncate">
            <p className="text-2xl font-medium truncate">
              {item?.company_name}
            </p>
            <p className="text-base leading-[18px] font-medium text-mid-gray w-full whitespace-pre-wrap line-clamp-2">
              {item?.description}
            </p>
          </div>
        )}

        {item?.address && (
          <p className="flex items-center gap-2 [&>span]:text-dark-gray">
            <span className="icon icon-location" />
            <span className="whitespace-pre-wrap text-base line-clamp-2">
              {item?.address}
            </span>
          </p>
        )}
      </div>
      <div
        className={`m-2 ${item?.type === "LOCATION" && "flex items-center"}`}
      >
        <span
          className={`icon text-xl ${
            item?.is_favorite || item?.is_favorited
              ? "icon-heart-active text-primary"
              : "icon-heart text-mid-gray"
          }`}
          onClick={handleClick}
        />
      </div>
    </div>
  );
};

const Business = () => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [keyword, setKeyword] = useState("");

  const { data } = useGetBusinessDetailQuery(
    { id: params.id },
    { skip: !params.id },
  );

  const [trigger, { data: outletData }] = useLazyGetOutletOfBusinessQuery();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((searchTerm) => {
      trigger({ id: params.id, search: searchTerm });
    }, 500),
    [trigger, params.id],
  );

  // Gọi API khi keyword thay đổi
  useEffect(() => {
    if (params.id) {
      if (keyword.trim()) {
        debouncedSearch(keyword.trim());
      } else {
        // Khi xóa hết keyword → gọi lại API không có search
        trigger({ id: params.id });
      }
    }
  }, [keyword, params.id, debouncedSearch, trigger]);

  // Load lần đầu khi vào trang
  useEffect(() => {
    if (params.id) {
      trigger({ id: params.id });
    }
  }, [params.id, trigger]);

  const handleSearchChange = (value) => {
    setKeyword(value);
  };

  return (
    <div className="section-content flex flex-col max-w-[480px] relative">
      <div>
        <div className="h-[100px] absolute inset-0 top-0">
          <img
            className="w-full h-[100px] object-cover"
            src={`${process.env.REACT_APP_URL_IMAGE}/${data?.data?.avatar_data?.file_path}`}
            alt="No images available"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://t4.ftcdn.net/jpg/05/17/53/57/360_F_517535712_q7f9QC9X6TQxWi6xYZZbMmw5cnLMr279.jpg";
            }}
          />
        </div>
        <div className="h-[100px]" />
      </div>
      <div className="absolute top-5 left-5 z-30 size-10 flex items-center justify-center">
        <span
          onClick={() => navigate("/quest")}
          className={`icon icon-arrow-left text-white text-[18px] drop-shadow-svg_shadow cursor-pointer`}
        />
      </div>

      <Card item={{ ...data?.data, type: "BUSINESS" }} isTranslate />

      <div className="flex flex-col gap-[18px] mb-[18px]">
        <h5 className="text-primary font-[Figtree] text-xl font-bold not-italic leading-none">
          Outlets
        </h5>

        <Search
          className="w-full"
          placeholder={t("base.search")}
          searchQuery={keyword}
          onSearchChange={handleSearchChange}
        />

        {outletData?.data.map((item) => (
          <Card
            key={item.id}
            item={{
              ...item,
              avatar_data: data?.data.avatar_data,
              type: "LOCATION",
            }}
            business_id={params.id}
          />
        ))}
      </div>
    </div>
  );
};

export default Business;
